import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getRandomContent, recordProgress, awardBonus, Content } from '../services/api';
import { speak } from '../services/audio';
import './Games.css';

const SpellingGame: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const lessonId = searchParams.get('lessonId') || undefined;
  const customSetId = searchParams.get('customSetId') || undefined;
  const inputRef = useRef<HTMLInputElement>(null);

  const [items, setItems] = useState<Content[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [feedback, setFeedback] = useState<'correct' | 'wrong' | null>(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadItems = async () => {
      try {
        const data = await getRandomContent(10, lessonId, customSetId);
        setItems(data);
      } catch (err) {
        console.error('Failed to load content:', err);
      } finally {
        setLoading(false);
      }
    };
    loadItems();
  }, [lessonId, customSetId]);

  useEffect(() => {
    if (!loading && items.length > 0 && inputRef.current) {
      inputRef.current.focus();
    }
  }, [loading, currentIndex, items.length]);

  const normalizeAnswer = (text: string): string => {
    return text
      .toLowerCase()
      .replace(/[.,!?;:'"]/g, '') // Remove punctuation
      .replace(/\s+/g, ' ')        // Normalize multiple spaces to single
      .trim();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (feedback) return;

    const currentItem = items[currentIndex];
    const isCorrect = normalizeAnswer(userInput) === normalizeAnswer(currentItem.english);

    setFeedback(isCorrect ? 'correct' : 'wrong');
    await recordProgress(currentItem.id, isCorrect);
    
    if (isCorrect) {
      speak(currentItem.english);
    }

    setScore(prev => ({
      correct: prev.correct + (isCorrect ? 1 : 0),
      total: prev.total + 1
    }));
  };

  const handleNext = () => {
    if (currentIndex < items.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setUserInput('');
      setFeedback(null);
    } else {
      handleComplete();
    }
  };

  const handleComplete = async () => {
    if (score.correct + 1 === items.length) {
      await awardBonus(30, 'perfect_spelling');
    }
    setCompleted(true);
  };

  if (loading) return <div className="loading">Ładowanie...</div>;
  if (items.length < 4) {
    return (
      <div className="game-error">
        <h2>Za mało słówek!</h2>
        <p>Potrzebujesz minimum 4 słówek do tej gry.</p>
        <button className="btn btn-primary" onClick={() => navigate('/games')}>Wróć</button>
      </div>
    );
  }

  const currentItem = items[currentIndex];
  if (!currentItem) return <div className="loading">Ładowanie...</div>;

  if (completed) {
    const isPerfect = score.correct === items.length;
    return (
      <div className="game-complete fade-in">
        <div className="complete-icon">{isPerfect ? '🏆' : '🎉'}</div>
        <h1>{isPerfect ? 'Perfekcyjnie!' : 'Świetnie!'}</h1>
        <p>Wynik: {score.correct} / {score.total}</p>
        {isPerfect && <div className="bonus-badge bounce">+30 bonus!</div>}
        <button className="btn btn-primary btn-large" onClick={() => window.location.reload()}>
          Zagraj ponownie
        </button>
      </div>
    );
  }

  return (
    <div className="game-container fade-in">
      <div className="game-header">
        <h2>✏️ Pisownia</h2>
        <div className="game-progress">{currentIndex + 1} / {items.length}</div>
      </div>

      <div className="game-score">✅ {score.correct} / {score.total}</div>

      <div className="spelling-card">
        <div className="polish-word">{currentItem.polish}</div>
        <p className="quiz-instruction">Wpisz to słowo po angielsku:</p>
        
        <form onSubmit={handleSubmit} className="spelling-form">
          <input
            ref={inputRef}
            type="text"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            className={`spelling-input ${feedback || ''}`}
            placeholder="Wpisz po angielsku..."
            disabled={!!feedback}
            autoComplete="off"
            autoCapitalize="off"
          />
          <button type="submit" className="btn btn-primary" disabled={!userInput.trim() || !!feedback}>
            Sprawdź ➜
          </button>
        </form>

        {feedback === 'wrong' && (
          <div className="correct-answer">
            Poprawna odpowiedź: <strong>{currentItem.english}</strong>
            <button className="speak-btn-inline" onClick={() => speak(currentItem.english)}>🔊</button>
          </div>
        )}

        {feedback && (
          <button className="btn btn-primary btn-next" onClick={handleNext}>
            Dalej ➜
          </button>
        )}
      </div>
    </div>
  );
};

export default SpellingGame;

